import { TILE } from '../sim/constants';
import { screenToWorldX, screenToWorldY, tileCenterWorld } from './projection';
import type { Camera, Viewport } from './projection';

export const MIN_ZOOM = 10;
export const MAX_ZOOM = 64;
export const DEFAULT_ZOOM = 30;

export interface MapSize {
    readonly w: number;
    readonly h: number;
}

function clampAxis(center: number, visibleWorld: number, worldSize: number): number {
    // כשכל המפה נכנסת למסך אין מה להזיז — ממרכזים אותה ומונעים "נדנוד" של המצלמה.
    if (visibleWorld >= worldSize) return worldSize / 2;
    const half = visibleWorld / 2;
    return Math.min(Math.max(center, half), worldSize - half);
}

/** מחזיק את המפה בתוך התצוגה. אין גלילה אל מעבר לקצה — זה מבלבל ונראה כמו באג. */
export function clampCamera(cam: Camera, vp: Viewport, map: MapSize): Camera {
    const zoom = Math.min(Math.max(cam.zoom, MIN_ZOOM), MAX_ZOOM);
    return {
        zoom,
        x: clampAxis(cam.x, (vp.width / zoom) * TILE, map.w * TILE),
        y: clampAxis(cam.y, (vp.height / zoom) * TILE, map.h * TILE),
    };
}

/** הזזה לפי פיקסלי מסך — כיוון הגרירה הפוך לכיוון תנועת המצלמה. */
export function panCamera(
    cam: Camera,
    deltaScreenX: number,
    deltaScreenY: number,
    vp: Viewport,
    map: MapSize,
): Camera {
    return clampCamera(
        {
            zoom: cam.zoom,
            x: cam.x - (deltaScreenX / cam.zoom) * TILE,
            y: cam.y - (deltaScreenY / cam.zoom) * TILE,
        },
        vp,
        map,
    );
}

/**
 * זום סביב נקודת עוגן על המסך: הנקודה בעולם שנמצאת מתחת לסמן (או בין שתי האצבעות)
 * נשארת מתחתיו. בלי זה הזום "בורח" והמשתמש מאבד את מה שהסתכל עליו.
 */
export function zoomCameraAt(
    cam: Camera,
    factor: number,
    anchorScreenX: number,
    anchorScreenY: number,
    vp: Viewport,
    map: MapSize,
): Camera {
    const zoom = Math.min(Math.max(cam.zoom * factor, MIN_ZOOM), MAX_ZOOM);
    if (zoom === cam.zoom) return cam;

    const worldX = screenToWorldX(anchorScreenX, cam, vp);
    const worldY = screenToWorldY(anchorScreenY, cam, vp);

    return clampCamera(
        {
            zoom,
            x: worldX - ((anchorScreenX - vp.width / 2) / zoom) * TILE,
            y: worldY - ((anchorScreenY - vp.height / 2) / zoom) * TILE,
        },
        vp,
        map,
    );
}

export function cameraOnTile(tileX: number, tileY: number, zoom = DEFAULT_ZOOM): Camera {
    return { x: tileCenterWorld(tileX), y: tileCenterWorld(tileY), zoom };
}
